import React, {useState, useMemo, useCallback} from 'react'
import {useTranslation, Trans} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {Box, Typography} from '@mui/material'
import {PGButton, getProductLineColors} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {ReactComponent as AssessmentCompleteIcon} from '@progyny/assets/icons/assessment-complete.svg'
import ProgressBar from '@progyny/components/ProgressBar'
import {
  useCompleteOnboardingQuestionnaireMutation,
  useSkipOnboardingQuestionnaireMutation,
} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {useProgressColor} from '@progyny/hooks/useProgressColor'
import {QuestionRenderer} from '@progyny/pages/Consultations/components/questions'
import type {FormPage} from '@progyny/pages/Consultations/components/questions'

export type {
  FormPage,
  FormQuestion,
  RadioQuestion,
  CheckboxQuestion,
  RatingTableQuestion,
  TextAreaQuestion,
  RadioOption,
  RatingTableItem,
  RatingTableColumn,
} from '@progyny/pages/Consultations/components/questions'

const useStyles = makeStyles<{colors?: {primary: string}}>()((_, {colors}) => ({
  outlinedButton: {
    boxShadow: 'none !important',
    appearance: 'none',
    transform: 'translateZ(0)',
    borderColor: `${colors?.primary || '#148199'} !important`,
    '&:hover': {
      boxShadow: 'none !important',
      borderColor: `${colors?.primary || '#148199'} !important`,
    },
    '&.Mui-disabled': {
      boxShadow: 'none !important',
      borderColor: '#D6D8E0 !important',
    },
  },
}))

export interface AssessmentFormBuilderProps {
  consultationId: string
  consultationSlug: string
  basePath?: string
  pages: FormPage[]
}

const AssessmentFormBuilder: React.FC<AssessmentFormBuilderProps> = ({
  consultationId,
  consultationSlug,
  basePath,
  pages,
}) => {
  const history = useHistory()
  const {t} = useTranslation()
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const progressColor = useProgressColor()
  const {classes} = useStyles({colors})

  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [isSkipping, setIsSkipping] = useState(false)

  const [completeQuestionnaire] = useCompleteOnboardingQuestionnaireMutation()
  const [skipQuestionnaire] = useSkipOnboardingQuestionnaireMutation()

  const handleInputChange = useCallback((questionId: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: value,
    }))
  }, [])

  const currentPage = pages[currentPageIndex]
  const isFirstPage = currentPageIndex === 0
  const isLastPage = currentPageIndex === pages.length - 1
  const progressValue = ((currentPageIndex + 1) / pages.length) * 100

  const questionDefinitions = useMemo(() => {
    const definitions: {id: string; question: string; group: string}[] = []
    pages.forEach((page) => {
      page.questions.forEach((q) => {
        if (q.type === 'rating_table') {
          q.items.forEach((item) => {
            definitions.push({
              id: `${q.id}_${item.id}`,
              question: `${q.question} - ${item.label}`,
              group: q.group,
            })
          })
        } else {
          definitions.push({
            id: q.id,
            question: q.question,
            group: q.group,
          })
        }
      })
    })
    return definitions
  }, [pages])

  const isCurrentPageValid = useMemo(() => {
    if (!currentPage) return false

    return currentPage.questions.every((question) => {
      if (question.required === false) return true

      if (question.type === 'rating_table') {
        return question.items.every((item) => {
          const fieldId = `${question.id}_${item.id}`
          const value = formData[fieldId]
          return value !== undefined && value !== ''
        })
      }

      const value = formData[question.id]
      return value !== undefined && value !== ''
    })
  }, [currentPage, formData])

  const handleBack = () => {
    if (!isFirstPage) {
      setCurrentPageIndex((prev) => prev - 1)
    }
  }

  const handleNext = async () => {
    if (isLastPage) {
      await handleSubmit()
    } else {
      setCurrentPageIndex((prev) => prev + 1)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)

    try {
      const responses = Object.entries(formData).map(([id, answer]) => {
        const definition = questionDefinitions.find((q) => q.id === id)
        return {
          question: definition?.question || id,
          answer,
          group: definition?.group || '',
        }
      })

      const result = await completeQuestionnaire({
        variables: {
          consultationId,
          responses: JSON.stringify(responses),
        },
      })

      if (result.data?.completeOnboardingQuestionnaire?.success) {
        setIsCompleted(true)
      }
    } catch (error) {
      console.error('Error completing questionnaire:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBookOnboarding = () => {
    if (basePath) {
      history.push(`${basePath}/onboarding`)
    } else {
      history.push(`/consultations/${consultationSlug}/onboarding`)
    }
  }

  const handleSkip = async () => {
    setIsSkipping(true)
    try {
      const result = await skipQuestionnaire({
        variables: {consultationId},
      })

      if (result.data?.skipOnboardingQuestionnaire?.success) {
        handleBookOnboarding()
      } else {
        console.error(
          'Skip failed:',
          result.data?.skipOnboardingQuestionnaire?.message
        )
      }
    } catch (error) {
      console.error('Error skipping questionnaire:', error)
    } finally {
      setIsSkipping(false)
    }
  }

  if (isCompleted) {
    return (
      <Box width='100%' maxWidth='1020px' mx='auto' pb={10}>
        <Box
          sx={{
            borderRadius: '16px',
            boxShadow: '0px 3px 16px 0px #BEC0C63D',
            padding: '40px',
            backgroundColor: 'background.paper',
            textAlign: 'center',
          }}
        >
          <Box
            mb={4}
            display='flex'
            justifyContent='center'
            sx={{
              '& svg path:first-of-type': {fill: colors.primary, opacity: 0.2},
              '& svg path:last-of-type': {fill: colors.primary},
            }}
          >
            <AssessmentCompleteIcon />
          </Box>

          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '150%',
              color: '#251F1F',
              mb: 2,
            }}
          >
            <Trans i18nKey='consultations_assessment_form_builder_thank_you_for_completing_assessm' />
          </Typography>

          <Typography
            sx={{
              fontFamily: 'DM Sans',
              fontSize: '16px',
              lineHeight: '150%',
              color: '#251F1F',
              mb: 4,
              maxWidth: '640px',
              mx: 'auto',
            }}
          >
            <Trans i18nKey='consultations_assessment_form_builder_now_take_next_step_by' />
          </Typography>

          <PGButton
            onClick={handleBookOnboarding}
            label={t('consultations_assessment_book_onboarding_call')}
            size='medium'
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box width='100%' maxWidth='1020px' mx='auto' pb={10}>
      <Box
        sx={{
          borderRadius: '16px',
          boxShadow: '0px 3px 16px 0px #BEC0C63D',
          padding: '40px',
          backgroundColor: 'background.paper',
        }}
      >
        <Box mb='40px'>
          <ProgressBar value={progressValue} progressColor={progressColor} />
        </Box>

        <Box display='flex' flexDirection='column' gap={4}>
          {currentPage?.questions.map((question) => (
            <QuestionRenderer
              key={question.id}
              question={question}
              formData={formData}
              handleInputChange={handleInputChange}
              isSubmitting={isSubmitting}
              colors={colors}
            />
          ))}
        </Box>

        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{maxWidth: '640px', margin: '0 auto', marginTop: 4}}
        >
          <PGButton
            variant='text'
            onClick={handleSkip}
            disabled={isSubmitting || isSkipping}
            label={t('consultations_assessment_skip_assessment')}
            size='small'
          />

          <Box display='flex' gap='20px'>
            <PGButton
              variant='outlined'
              onClick={handleBack}
              disabled={isFirstPage || isSubmitting}
              label={t('common_back')}
              size='medium'
              className={classes.outlinedButton}
            />
            <PGButton
              onClick={handleNext}
              disabled={!isCurrentPageValid || isSubmitting}
              label={
                isLastPage
                  ? isSubmitting
                    ? t('consultations_assessment_submitting')
                    : t('consultations_assessment_complete')
                  : t('common_continue')
              }
              size='medium'
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default AssessmentFormBuilder
